import { gql, useMutation } from "@apollo/client";
import { client } from "../apolloClient";

/**
 * useActivityApi custom hook for activity API operations.
 * - Provides CRUD operations for activities using Apollo Client and GraphQL.
 * - Handles cache updates for activity queries and mutations.
 * - Exposes helpers for reading/writing/updating activities in Apollo cache.
 * - Returns methods for fetching, creating, updating, and deleting activities.
 * - Ensures UI stays in sync with backend by updating Apollo cache after mutations.
 */

// GraphQL Queries and Mutations
const MILESTONE_ACTIVITY_QUERIES = {

    // Query to fetch milestone activity mappings by project and task
    GET_MILESTONE_ACTIVITY_BY_PROJECT_AND_TASK: gql`
    query GetMilestoneActivityByProjectAndTask($project_id: Int!, $task_id: ID!) {
      GetMilestoneActivityByProjectAndTask(project_id: $project_id, task_id: $task_id) {
        task_id
        task_name
        project_id
        milestone_code
        activities {
          milestone_activity_mapping_id
          activity_id
          activity_name
          created_on
          updated_on
        }
      }
    }
  `,

    // Query to fetch a activity by ID
    GET_MILESTONE_ACTIVITY: gql`
    query GetMilestoneActivityById($milestone_id: ID!) {
      GetMilestoneActivityById(milestone_id: $milestone_id) {
          milestone_id
          project_id
          start_date
          end_date
          activities {
            activity_id
            activity_name
            remove_flag
            tags {
              tag_id
              tag_name
            }
           }   
          is_active
          created_on
          updated_on
      }
    }
  `,

  GET_MILESTONE_ACTIVITIES_BY_IDS: gql`
    query GetMilestoneActivitiesByIds($milestone_id: [ID!]!) {
      GetMilestoneActivitiesByIds(milestone_id: $milestone_id) {
        milestone_id
        project_id
        start_date
        end_date
        activities {
          activity_id
          activity_name
          remove_flag
          tags {
            tag_id
            tag_name
          }
        }
        is_active
        created_on
        updated_on
      }
    }
  `,

    // Mutation to add a new activity
    ADD_MILESTONE_ACTIVITY: gql`
    mutation AddMilestoneActivity(
      $milestone_id: ID!
      $activity_id: [ID]!
    ) {
      AddMilestoneActivity(
        milestone_id: $milestone_id
        activity_id: $activity_id
      ) {
          milestone_id
          project_id
          start_date
          end_date
          activities {
            activity_id
            activity_name
            remove_flag
            tags {
              tag_id
              tag_name
            }
           }   
          is_active
          created_on
          updated_on
      }
    }
  `,

    // Mutation to update an existing activity
    UPDATE_MILESTONE_ACTIVITY: gql`
    mutation UpdateMilestoneActivity(
      $milestone_id: ID!
      $activity_id: ID!
      $activity_name: String
      $tag_id: [ID!]
    ) {
      UpdateMilestoneActivity(
        milestone_id: $milestone_id
        activity_id: $activity_id
      ) {
        milestone_id
          project_id
          start_date
          end_date
          activities {
            activity_id
            activity_name
            activity_code
            tags {
              tag_id
              tag_name
            }
           }   
          is_active
          created_on
          updated_on
      }
    }
  `,

    // Mutation to delete a activity
    DELETE_MILESTONE_ACTIVITY: gql`
    mutation DeleteMilestoneActivity($milestone_id: ID!) {
      DeleteMilestoneActivity(milestone_id: $milestone_id) {
       milestone_id
          project_id
          start_date
          end_date
          activities {
            activity_id
            activity_name
            activity_code
            tags {
              tag_id
              tag_name
            }
           }   
          is_active
          created_on
          updated_on
      }
    }
  `,
};

/**
 * Cache operations for activities.
 * - readActivities: Reads activities from Apollo cache.
 * - writeActivities: Writes activities to Apollo cache.
 * - updateActivityInCache: Updates a activity in Apollo cache.
 */
// (Intentionally left commented/removed: GET_ACTIVITIES and getActivities -- this hook does NOT expose them)

export const useMilestoneActivityApi = () => {
    // Mutation for adding a activity
    const [addMilestoneActivityMutation] = useMutation(MILESTONE_ACTIVITY_QUERIES.ADD_MILESTONE_ACTIVITY);

    // Mutation for updating a activity
    const [updateMilestoneActivityMutation] = useMutation(MILESTONE_ACTIVITY_QUERIES.UPDATE_MILESTONE_ACTIVITY);

    // Mutation for deleting a activity
    const [deleteActivityMutation] = useMutation(MILESTONE_ACTIVITY_QUERIES.DELETE_MILESTONE_ACTIVITY);

    /**
     * Fetch milestone activity mappings by project and task.
     */
    const getMilestoneActivityByProjectAndTask = async (project_id, task_id) => {
        try {
            const { data } = await client.query({
                query: MILESTONE_ACTIVITY_QUERIES.GET_MILESTONE_ACTIVITY_BY_PROJECT_AND_TASK,
                variables: { project_id, task_id },
                fetchPolicy: "network-only",
            });
            return { success: true, data: data.GetMilestoneActivityByProjectAndTask };
        } catch (error) {
            console.error("Error fetching milestone activity by project and task:", error);
            return { success: false, error: error.message };
        }
    };

   const getMilestoneActivitiesByIds = async(milestone_id)=>{
    try{
      const { data } = await client.query({
        query: MILESTONE_ACTIVITY_QUERIES.GET_MILESTONE_ACTIVITIES_BY_IDS,
        variables: { milestone_id },
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetMilestoneActivitiesByIds };
    } catch (error) {
      console.error("Error fetching milestone activities by IDs:", error);
      return { success: false, error: error.message };
    }
   } 

    /**
     * Fetch a milestone's activities by milestone_id.
     */
    const getMilestoneActivityById = async (milestone_id) => {
        try {
            const { data } = await client.query({
                query: MILESTONE_ACTIVITY_QUERIES.GET_MILESTONE_ACTIVITY,
                variables: { milestone_id },
                fetchPolicy: "network-only",
            });
            return { success: true, data: data.GetMilestoneActivityById };
        } catch (error) {
            console.error("Error fetching activity:", error);
            return { success: false, error: error.message };
        }
    };

    /**
     * Creates a new milestone activity mapping.
     */
    const createMilestoneActivity = async (milestone_id,activity_id) => {
        try {
            const { data } = await addMilestoneActivityMutation({
                variables: {
                    milestone_id: milestone_id,
                    activity_id: activity_id
                },
            });
            return { success: true, data: data.AddMilestoneActivity };
        } catch (error) {
            console.error("Error creating activity:", error);
            return { success: false, error: error.message };
        }
    };

    /**
     * Updates an existing milestone activity mapping.
     */
    const updateMilestoneActivity = async (activityId, formData) => {
        try {
            const { data } = await updateMilestoneActivityMutation({
                variables: {
                    activity_id: activityId,
                    milestone_id: formData.milestone_id
                },
            });
            return { success: true, data: data.UpdateMilestoneActivity };
        } catch (error) {
            console.error("Error updating activity:", error);
            return { success: false, error: error.message };
        }
    };

    /**
     * Deletes a milestone activity mapping by its milestone_id.
     */
    const deleteMilestoneActivity = async (milestone_id) => {
        try {
            const { data } = await deleteActivityMutation({
                variables: { milestone_id },
            });
            return { success: true, data: data.DeleteMilestoneActivity };
        } catch (error) {
            console.error("Error deleting activity:", error);
            return { success: false, error: error.message };
        }
    };

    return {
        getMilestoneActivityById,
        getMilestoneActivityByProjectAndTask,
        createMilestoneActivity,
        updateMilestoneActivity,
        deleteMilestoneActivity,
        getMilestoneActivitiesByIds
    };
};
